import { Input } from '@/components/inputs';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuInfo, LuStar, LuUsers } from 'react-icons/lu';
import { WizardNav } from '../components/WizardNav';
import { useNewCommunicationActions } from './context/useNewCommunicationActions';
import { useNewCommunicationValues } from './context/useNewCommunicationValues';

export const RecipientsRecap: FC = () => {
  const { t } = useTranslation('comuni');
  const { selectedIds, counts, stale, isEstimating, isAdvancing, suggestedName, zone, geo } = useNewCommunicationValues();
  // citizens the zone can never match: without a usable address for the chosen kind of zone (per-list sums, indicative)
  const geoExcluded = zone && geo.status === 'ready' ? Math.max(0, geo.total - (zone.kind === 'polygon' ? geo.onMap : geo.withStreet)) : 0;
  const { handleSaveAsList, handleRecipientsNext } = useNewCommunicationActions();

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  const onSave = async () => {
    const ok = await handleSaveAsList(saveName);
    if (ok) {
      setSaveOpen(false);
      setSaveName('');
    }
  };

  if (!selectedIds.length) {
    return (
      <>
        <div className='rounded-[14px] border-[1.5px] border-dashed p-6 text-center text-[14.5px] text-quaternary-400'>{t('composer.noSelection')}</div>
        <WizardNav primaryText={t('composer.next')} onPrimary={handleRecipientsNext} primaryDisabled />
      </>
    );
  }

  return (
    <>
      <div className='flex flex-wrap items-center gap-5 rounded-[14px] border px-6 py-5'>
        <span className='flex size-[46px] flex-none items-center justify-center rounded-full bg-brand-50'>
          <LuUsers className='size-6 text-brand-700' />
        </span>
        <div className='min-w-[220px] flex-1'>
          <div className='text-[22px] font-extrabold tracking-tight text-primary-900'>
            <span className='text-brand-700'>~{isEstimating ? '…' : (counts?.total ?? '—')}</span> {t('composer.selectedCount')}
          </div>
          <div className='mt-0.5 text-sm text-tertiary-600'>{t('composer.inCurrentSelection')}</div>
        </div>
        <div className='flex flex-wrap gap-2.5'>
          <span className='flex items-center gap-2 rounded-full border bg-secondary-background px-4 py-2 text-[13.5px] font-bold text-primary-900'>
            <span className='size-[9px] rounded-full bg-brand-700' /> {t('composer.withPhone')} ·{' '}
            <span className='tabular-nums'>{isEstimating ? '…' : (counts?.withPhone ?? '—')}</span>
          </span>
          <span className='flex items-center gap-2 rounded-full border bg-secondary-background px-4 py-2 text-[13.5px] font-bold text-primary-900'>
            <span className='size-[9px] rounded-full bg-cyan-700' /> {t('composer.withEmail')} ·{' '}
            <span className='tabular-nums'>{isEstimating ? '…' : (counts?.withEmail ?? '—')}</span>
          </span>
        </div>
        <div className='relative flex-none'>
          <button
            type='button'
            onClick={() => setSaveOpen((open) => !open)}
            className='flex items-center gap-2 rounded-[10px] border border-brand-200 bg-white-100 px-4 py-2.5 text-[13.5px] font-bold text-brand-700 hover:bg-brand-50'
          >
            <LuStar className='size-3.5' /> {t('composer.saveAsList')}
          </button>
          {saveOpen && (
            <div className='absolute right-0 top-[calc(100%+10px)] z-50 w-[380px] rounded-[14px] border bg-white-100 p-5 text-left shadow-xl'>
              <div className='text-[15px] font-extrabold text-primary-900'>{t('composer.saveAsList')}</div>
              <div className='mb-1.5 mt-3.5 text-[11.5px] font-bold uppercase tracking-wide text-quaternary-400'>{t('composer.listName')}</div>
              <Input
                id='comuni-save-name'
                name='comuni-save-name'
                type='text'
                className='w-full px-3 py-2.5'
                value={saveName || suggestedName}
                onChange={(ev) => setSaveName((ev.target as HTMLInputElement).value)}
              />
              <div className='mt-4 flex justify-end gap-2.5'>
                <button
                  type='button'
                  onClick={() => setSaveOpen(false)}
                  className='rounded-[9px] border bg-white-100 px-4 py-2 text-[13.5px] font-bold text-primary-900 hover:bg-secondary-background'
                >
                  {t('composer.saveCancel')}
                </button>
                <button
                  type='button'
                  onClick={onSave}
                  className='rounded-[9px] bg-brand-700 px-5 py-2 text-[13.5px] font-bold text-white-100 hover:bg-brand-800'
                >
                  {t('composer.saveConfirm')}
                </button>
              </div>
            </div>
          )}
        </div>
        {!isEstimating && counts != null && counts.unreachable > 0 && (
          <div className='flex basis-full items-start gap-2 border-t pt-3 text-[13.5px] leading-relaxed text-tertiary-600'>
            <LuInfo className='mt-0.5 size-4 flex-none text-brand-700' />
            <span>
              <b className='text-primary-900'>{counts.unreachable}</b> {t('composer.unreachableWarning')}
            </span>
          </div>
        )}
        {geoExcluded > 0 && (
          <div className='flex basis-full items-start gap-2 border-t pt-3 text-[13.5px] leading-relaxed text-tertiary-600'>
            <LuInfo className='mt-0.5 size-4 flex-none text-brand-700' />
            <span>
              <b className='text-primary-900'>{geoExcluded}</b> {t('composer.geo.excluded')}
            </span>
          </div>
        )}
        {stale && counts && <p className='basis-full text-sm text-yellow-700'>{t('composer.stale')}</p>}
      </div>

      <WizardNav primaryText={t('composer.next')} onPrimary={handleRecipientsNext} primaryDisabled={!selectedIds.length || isEstimating || isAdvancing} />
    </>
  );
};
